﻿import React, { useEffect, useRef, useState } from 'react';

export interface SegmentedOption<T extends string | number> {
    value: T;
    label: React.ReactNode;
}

export interface SegmentedControlProps<T extends string | number> {
    options: SegmentedOption<T>[];
    value: T;
    onChange: (value: T) => void;
    disabled?: boolean;
    title?: string;
    className?: string;
}

export function SegmentedControl<T extends string | number>({
    options,
    value,
    onChange,
    disabled,
    title,
    className
}: SegmentedControlProps<T>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [initialized, setInitialized] = useState(false);
    const labelKey = options.map(o => String(o.label)).join('|');

    useEffect(() => {
        const container = containerRef.current;
        if (!container) { return; }
        const active = container.querySelector('button[aria-pressed="true"]') as HTMLElement | null;
        if (!active) { return; }
        container.style.setProperty('--sc-left', `${active.offsetLeft}px`);
        container.style.setProperty('--sc-width', `${active.offsetWidth}px`);
        if (!initialized) {
            requestAnimationFrame(() => setInitialized(true));
        }
    }, [value, initialized, labelKey]);

    return (
        <div
            ref={containerRef}
            title={title}
            className={`fe-cw-segmented${initialized ? ' fe-cw-segmented--ready' : ''}${disabled ? ' fe-cw-segmented--disabled' : ''}${className ? ` ${className}` : ''}`}>
            {options.map((opt) => (
                <button
                    key={opt.value}
                    type="button"
                    aria-pressed={opt.value === value}
                    disabled={disabled}
                    onClick={() => onChange(opt.value)}>
                    {opt.label}
                </button>
            ))}
        </div>
    );
}
